/**
 * Fetches POC assignment group metadata from Firestore for configuration UIs
 * (`/configurations/pharmacy_order_updates/metadata/poc_assignment_groups`).
 */

import { useCallback, useEffect, useState } from "react";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import type {
  PocAssignmentGroupEntry,
  PocAssignmentGroupsMetadataDoc,
} from "../../../../../data-model/pocAssignmentGroupsMetadata";

/**
 * Loads assignment group rows (name + batches) from the poc_assignment_groups doc.
 *
 * @returns Current entries, load state, optional error string, and a refetch helper.
 */
export function usePocAssignmentGroupsMetadata() {
  const [entries, setEntries] = useState<PocAssignmentGroupEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const doc =
        await FirestoreService.getDocument<PocAssignmentGroupsMetadataDoc>(
          FirestoreDocumentReference.pharmacyOrderUpdatesPocAssignmentGroups(),
        );
      setEntries(
        Array.isArray(doc.data)
          ? doc.data.map((e) => ({
              group_name: e.group_name ?? "",
              batches: Array.isArray(e.batches) ? [...e.batches] : [],
            }))
          : [],
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes("does not exist")) {
        setEntries([]);
      } else {
        setError(message);
        setEntries([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { entries, loading, error, refetch: load };
}
